import { useMemo } from 'react'
import { DEFAULT_TIMEZONE } from 'Data/constants.js'
import {
  isUnscheduledAppointment,
  formatTzDate,
  parseLocalDatetimeStringToZonedDate,
} from 'Data/format.js'
import { utcToZonedTime } from 'date-fns-tz'

/** @type {import('Simple/types.js').useDataTransform} */
export default function useDataTransform(props, data) {
  return useMemo(() => {
    if (!data?.length) return data

    let appointment = data[0]
    let timeZoneId = getTimeZoneId(appointment)
    let entries = []

    // First entry - appointment creation
    entries.push({
      id: appointment._id,
      action: 'CREATED',
      author: appointment.created_by?.person,
      datetime: getFormattedDatetime(appointment.sys_created, timeZoneId),
    })

    // Other entries - booking changes
    appointment.appointment_bookings.forEach((booking, index) => {
      for (let state_change of booking.state_changes) {
        let baseEntry = {
          id: state_change._id,
          datetime: getFormattedDatetime(state_change.change_time, timeZoneId),
          booking_datetime: getFormattedBookingStartTime(booking, timeZoneId),
        }

        if (state_change.new_state === 'SCHEDULED') {
          entries.push({
            ...baseEntry,
            action:
              appointment.appointment_bookings.length > 1 && index !== 0
                ? 'RESCHEDULED'
                : 'SCHEDULED',
            author: booking.created_by_person || booking.created_by?.person,
          })
        } else if (
          state_change.new_state === 'OFFICE_CANCELLED' ||
          state_change.new_state === 'PATIENT_CANCELLED'
        ) {
          entries.push({
            ...baseEntry,
            action: state_change.new_state,
            author: booking.modified_by_person || booking.modified_by?.person,
          })
        } else if (state_change.new_state === 'NO_SHOW') {
          entries.push({
            ...baseEntry,
            action: state_change.new_state,
            author: booking.modified_by?.person,
          })
        }
      }
    })
    return entries
  }, [data])
}

/**
 * Get timezone from treatment organization if unscheduled appointment
 * or from booking location if scheduled
 * @param {Object} appointment
 * @returns {String}
 */
function getTimeZoneId(appointment) {
  let timeZoneId = isUnscheduledAppointment(appointment)
    ? appointment.tx?.organization?.time_zone_id
    : appointment.booking?.[0]?.location?.time_zone_id
  return timeZoneId || DEFAULT_TIMEZONE
}

/**
 * Get action timestamp already formatted here
 * in order to use calculated timezone
 * @param {String} timestamp
 * @param {String} timeZoneId
 * @returns {String}
 */
function getFormattedDatetime(timestamp, timeZoneId) {
  return formatTzDate(
    utcToZonedTime(new Date(`${timestamp}Z`), timeZoneId),
    'MMM d, yyyy hh:mm a',
    timeZoneId
  )
}

/**
 * Get booking timestamp already formatted here
 * in order to use calculated timezone
 * @param {Object} booking
 * @param {String} timeZoneId
 * @returns {String}
 */
function getFormattedBookingStartTime(booking, timeZoneId) {
  if (!booking.local_start_date || !booking.local_start_time) return null
  return formatTzDate(
    parseLocalDatetimeStringToZonedDate(
      `${booking.local_start_date}T${booking.local_start_time}`,
      timeZoneId
    ),
    'MMM d, yyyy hh:mm a',
    timeZoneId
  )
}
